import { Character } from '../components/GeneralAppointment/types'

const CHARACTER_FRONT_VIEW_NEW_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/characters-front-view-new'
const CHARACTER_FRONT_VIEW_LEGACY_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/characters-front-view'
export const WORKER_CHARACTER_IDS = ['jane', 'kari', 'elias', 'elena'] as const

/** True for worker instances (building slots, modals) — use with characterName for labels */
export function isWorkerInstanceCharacter(character: unknown): boolean {
  if (!character || typeof character !== 'object') return false
  const c = character as { type?: string; characterId?: string }
  if (c.type === 'worker') return true
  const id = String(c.characterId || '').toLowerCase()
  return (WORKER_CHARACTER_IDS as readonly string[]).includes(id)
}

export const getCharacterFrontView = (characterId?: string, rarity?: string): string => {
  const id = characterId?.toLowerCase() || 'sakura'
  const rarityStr = rarity?.toLowerCase() || 'common'
  return `${CHARACTER_FRONT_VIEW_NEW_BASE}/${id}-${rarityStr}.webp`
}

/** Inventory-style single art (no rarity suffix); used when `-new/{id}-{rarity}.webp` is missing (e.g. child roster). */
export const getLegacyCharacterFrontView = (characterId?: string): string => {
  const id = characterId?.toLowerCase() || 'sakura'
  return `${CHARACTER_FRONT_VIEW_LEGACY_BASE}/${id}.webp`
}

/**
 * Honor claim `applied.honorCharacter.portraitUrl` (from server inventory) when present; else rarity art on CDN `-new`.
 */
export const getHonorRewardCharacterPortraitUrl = (
  characterId: string,
  rarity: string,
  portraitUrlFromApi?: string | null,
): string => {
  const trimmed = typeof portraitUrlFromApi === 'string' ? portraitUrlFromApi.trim() : ''
  if (trimmed.startsWith('https://')) return trimmed
  return getCharacterFrontView(characterId, rarity)
}

/**
 * Battle log / HUD circular portrait — half of the prior 72–80px building-stand match (~36–40px).
 */
export const BUILDING_CHARACTER_STAND_ORB_SIZE_CLASS =
  'h-[2.25rem] w-[2.25rem] min-h-[2.25rem] min-w-[2.25rem] sm:h-10 sm:w-10 sm:min-h-10 sm:min-w-10'

export const normalizeId = (id: any): string => {
  return String(id || '').trim()
}

/**
 * Resolve full inventory/API hero row from a slim roster `Character` (appointment carousel).
 */
export function findUserCharacterForAppointment(rosterChar: Character | null | undefined, userCharacters: any[]): any | null {
  if (!rosterChar || !Array.isArray(userCharacters) || userCharacters.length === 0) return null

  const id = normalizeId(rosterChar._id)
  if (id) {
    const byId = userCharacters.find((x: any) => normalizeId(x?._id) === id)
    if (byId) return byId
    const byIdLoose = userCharacters.find((x: any) => String(x?._id ?? '') === String(rosterChar._id ?? ''))
    if (byIdLoose) return byIdLoose
  }

  const rc = rosterChar as Character & { characterId?: string }
  const templateId = normalizeId(rc.characterTemplateId || rc.characterId)
  if (templateId) {
    const templateMatches = userCharacters.filter((x: any) => normalizeId(x?.characterId) === templateId)
    if (templateMatches.length === 1) return templateMatches[0]
    if (templateMatches.length > 1 && rosterChar.level != null) {
      const lv = Number(rosterChar.level)
      const byLevel = templateMatches.filter((x: any) => Number(x?.level) === lv)
      if (byLevel.length === 1) return byLevel[0]
    }
    return templateMatches[0] ?? null
  }

  return null
}

/** Coerce API stats for combat UI (attack / hp / armor / stamina). */
export function normalizeCombatStats(stats: unknown): {
  attackPower: number
  maxHp: number
  currentHp: number
  armor: number
  maxSp: number
} {
  const s = stats && typeof stats === 'object' ? (stats as Record<string, unknown>) : {}
  const num = (v: unknown, fallback = 0): number => {
    const n = Number(v)
    return Number.isFinite(n) ? n : fallback
  }
  const maxHp = num(s.maxHp, 0)
  const currentRaw = num(s.currentHp, NaN)
  return {
    attackPower: num(s.attackPower ?? s.attack, 0),
    maxHp,
    currentHp: Number.isFinite(currentRaw) ? currentRaw : maxHp,
    armor: num(s.armor, 0),
    maxSp: num(s.maxSp ?? s.maxStamina ?? s.stamina, 0),
  }
}

/** Hero slug for `/assets/appointment/{slug}.webp` (prefers API/template field). */
export function resolveAppointmentKingTemplateSlug(char: Character): string {
  const tid = char.characterTemplateId?.trim()
  if (tid) return tid.toLowerCase()
  const name = char.characterName?.replace(/\s+/g, '').trim() || ''
  if (name) return name.toLowerCase()
  return 'sakura'
}

export const isIdMatch = (id1: any, id2: any): boolean => {
  if (!id1 || !id2) return false
  const normalized1 = normalizeId(id1)
  const normalized2 = normalizeId(id2)
  return normalized1 !== '' && normalized2 !== '' && normalized1 === normalized2
}

export const getCharacterImage = (
  charData: any,
  charId: string,
  userCharacters: any[]
): string => {
  if (charData.characterImg) return charData.characterImg
  const userChar = userCharacters?.find(
    (c: any) => c._id === charId || c._id?.toString() === charId?.toString()
  ) as any
  if (userChar) {
    return getCharacterFrontView((userChar as any).characterId, (userChar as any).rarity)
  }
  return ''
}

export const createCharacterFromUserData = (userChar: any): Character => {
  const tid = (userChar as any).characterId
  return {
    _id: userChar._id,
    characterName:
      (userChar as any).characterName || userChar.characterId || (userChar as any).nickname || 'Unknown',
    characterImg: getCharacterFrontView((userChar as any).characterId, (userChar as any).rarity),
    level: userChar.level || 0,
    stats: {
      maxHp: (userChar.stats as any)?.maxHp || 100,
      currentHp: (userChar.stats as any)?.currentHp || 100,
    },
    rarity: (userChar as any).rarity || 'common',
    isMainCharacter: (userChar as any).isMainCharacter || false,
    ...(tid ? { characterTemplateId: String(tid).toLowerCase() } : {}),
  }
}

/** Convert any character-like object (from modal or API) to Character. Use this when the handler receives the selected character directly. */
export const toAppointmentCharacter = (char: any): Character => {
  if (!char || char._id == null) {
    throw new Error('Invalid character')
  }
  return {
    _id: char._id,
    characterName: char.characterName || char.characterId || char.nickname || 'Unknown',
    characterImg: char.characterImg || getCharacterFrontView(char.characterId, char.rarity || 'common'),
    level: Number(char.level) || 0,
    stats: char.stats && typeof char.stats === 'object'
      ? { maxHp: char.stats.maxHp ?? 100, currentHp: char.stats.currentHp ?? 100 }
      : { maxHp: 100, currentHp: 100 },
    rarity: char.rarity || 'common',
    isMainCharacter: Boolean(char.isMainCharacter),
    ...(char.characterId
      ? { characterTemplateId: String(char.characterId).toLowerCase() }
      : {}),
  }
}
